/**
 * Application constants
 */

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  WORKSPACE: '/workspace',
  SETTINGS: '/settings',
  LOGIN_CALLBACK: '/logins/callback',

  RUNS: '/runs',
  /** A project's page (`/projects/:projectId`), home of its Agent runs panel. */
  PROJECT: '/projects',
  DASHBOARDS: '/dashboards',

  /** Git credentials is the Settings index route, so it has no path of its own. */
  SETTINGS_PROFILE: '/settings/profile',
  SETTINGS_FEATURES: '/settings/features',

  /** Packs is the Provisioning index route, so it has no path of its own. */
  ADMIN_PROVISIONING: '/admin/provisioning',
  ADMIN_PROVISIONING_PROJECTS: '/admin/provisioning/projects',

  /** Features is the Registry index route, so it has no path of its own. */
  ADMIN_REGISTRY: '/admin/registry',
  ADMIN_REGISTRY_IMAGES: '/admin/registry/images',
  ADMIN_REGISTRY_AI_MODELS: '/admin/registry/ai-models',

  /** Git servers is the System index route, so it has no path of its own. */
  ADMIN_SYSTEM: '/admin/system',
  ADMIN_SYSTEM_LOGINS: '/admin/system/logins',

  /**
   * Legacy paths that redirect to their post-consolidation destinations.
   * The pre-consolidation keys these once duplicated (PROFILE, FEATURES,
   * ADMIN_FEATURES, ADMIN_PROJECTS, ADMIN_PACKS, ADMIN_IMAGES,
   * ADMIN_GIT_SERVERS, ADMIN_LOGIN_PROVIDERS) have been removed now that
   * nothing references them; these LEGACY_* keys remain so old bookmarked
   * URLs still resolve.
   *
   * Note LEGACY_ADMIN_FEATURES and LEGACY_ADMIN_PROJECTS share their literal
   * values with API_PATHS entries below. That is a coincidence of naming, not
   * a duplication: these are browser URLs, those are backend endpoints. They
   * are free to diverge and must not be collapsed into shared constants.
   */
  LEGACY_PROFILE: '/profile',
  LEGACY_FEATURES: '/features',
  LEGACY_ADMIN_FEATURES: '/admin/features',
  LEGACY_ADMIN_PROJECTS: '/admin/projects',
  LEGACY_ADMIN_PACKS: '/admin/packs',
  LEGACY_ADMIN_IMAGES: '/admin/images',
  LEGACY_ADMIN_GIT_SERVERS: '/admin/git-servers',
  LEGACY_ADMIN_LOGIN_PROVIDERS: '/admin/login-providers',
} as const;

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'sanctum_access_token',
  REFRESH_TOKEN: 'sanctum_refresh_token',
  CODE_VERIFIER: 'sanctum_code_verifier',
  AUTH_STATE: 'sanctum_auth_state',
  WORKSPACE_VIEW_MODE: 'sanctum_workspace_view_mode',
  LOGIN_FLOW: 'sanctum_login_flow',
} as const;

/**
 * Backend endpoint paths. Unrelated to ROUTES above even where the literals
 * match — these address the API, those address the browser.
 */
export const API_PATHS = {
  AUTH: '/auth',
  SETTINGS: '/settings',
  FEATURES_PREFERENCES: '/features/preferences',
  ADMIN_FEATURES: '/admin/features',
  ADMIN_PROJECTS: '/admin/projects',
  ADMIN_ASSIGNMENTS: '/admin/assignments',
  ADMIN_IMAGE_CACHE: '/admin/image-cache',
  ADMIN_BASE_IMAGE: '/admin/base-image',
  ADMIN_PACKS: '/admin/packs',
  LOGINS: '/logins',
  ADMIN_LOGINS: '/admin/logins',
  AGENT_RUNS: '/agent-runs',
  /** A project's Agent runs panel (backend `project_router`). */
  projectAgentRuns: (projectId: string): string =>
    `/projects/${encodeURIComponent(projectId)}/agent-runs`,
} as const;
